import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { getById, update } from '../api';
import { getContractors } from '../api/contractorsAPI';
import { getHierarchyStructureById } from '../api/hierarchy-structureAPI';
import { getPurchaseOrderLinesByPurchaseOrderId } from '../api/purchase-order-linesAPI';
import { getPaginatedPurchaseOrders, getPurchaseOrderById } from '../api/purchase-orderAPI';
import { getWorkOrderCommentsByWorkOrderId } from '../api/work-order-commentsAPI';
import { formatIntDateTime } from '../utils/dateUtils';
import { getGoogleGeocode, getGooglePlaces, getGoogleTileUrl } from '../api/google-mapsAPI';
import { getAssetLocationByAssetId } from '../api/asset-locationAPI';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import PurchaseOrderPrintView from './PurchaseOrderPrintView';
import WorkOrderCommentsSection from './WorkOrderCommentsSection';
import WorkOrderShare from './WorkOrderShare';
import { getTechnicians } from '../api/techniciansAPI';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const AUSTRALIA_DEFAULT_CENTER = [-25.2744, 133.7751];
const AUSTRALIA_DEFAULT_ZOOM = 4;
const DEFAULT_USER_AVATAR = `${process.env.REACT_APP_SPACES_BASE}/user.png`;

const profileImageUrl = (value) => {
  if (!value) return DEFAULT_USER_AVATAR;
  if (String(value).startsWith('http://') || String(value).startsWith('https://')) return value;
  return `${process.env.REACT_APP_SPACES_BASE}/${value}`;
};

const completionImageUrl = (image) => {
  const value = typeof image === 'string' ? image : image?.filename || image?.name || image?.url;
  if (!value) return null;
  if (String(value).startsWith('http://') || String(value).startsWith('https://')) return value;
  return `${process.env.REACT_APP_SPACES_BASE}/${value}`;
};

const WorkOrderDetails = ({
  workOrder,
  crew = [],
  statuses = [],
  currentStatus,
  onStatusChange,
  showAssignContractor = 1,
  showCurrentStatus = null,
  parts = [],
  labour = [],
  services = [],
  equipment = [],
  forms = [],
  actualCosts = {},
  onViewForm,
  onAddForm,
  canUpdateWorkOrder = false,
  onJobReadyStatusChange,
}) => {
  const [showPurchaseOrderDetailsModal, setShowPurchaseOrderDetailsModal] = useState(false);
  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState(null);
  const [purchaseOrderDetailLines, setPurchaseOrderDetailLines] = useState([]);
  const [isLoadingPurchaseOrderDetails, setIsLoadingPurchaseOrderDetails] = useState(false);
  const [localParts, setLocalParts] = useState(parts);
  const [activeTab, setActiveTab] = useState('general');
  const [locationCoords, setLocationCoords] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [editableMeetingPoint, setEditableMeetingPoint] = useState('');
  const [savedMeetingPoint, setSavedMeetingPoint] = useState('');
  const [editableLocation, setEditableLocation] = useState('');
  const [savedLocationAddress, setSavedLocationAddress] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharedWorkPackage, setSharedWorkPackage] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);
  const [hasLoadedLocationTab, setHasLoadedLocationTab] = useState(false);
  const [departmentHierarchyNames, setDepartmentHierarchyNames] = useState([]);
  const latestWorkOrderIdRef = useRef(workOrder?.id);
  const [workOrderComments, setWorkOrderComments] = useState([]);
  const [showAddCrewModal, setShowAddCrewModal] = useState(false);
  const [availableTechnicians, setAvailableTechnicians] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [savingCrew, setSavingCrew] = useState(false);
  const [localCrewIds, setLocalCrewIds] = useState([]);
  const workOrderCommentCount = workOrderComments.length;
  const [completedByDisplayName, setCompletedByDisplayName] = useState('');
  const userProfile = useUserProfile();



  const applyLocationDetails = useCallback((sourceWorkOrder) => {
    const meetingPoint = (sourceWorkOrder?.meetingPoint || '').trim();
    const address = (
      sourceWorkOrder?.locationAddress
      || sourceWorkOrder?.asset?.location?.locationAddress
      || ''
    ).trim();

    setEditableMeetingPoint(meetingPoint);
    setSavedMeetingPoint(meetingPoint);
    setEditableLocation(address);
    setSavedLocationAddress(address);
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
  }, []);

  const hasDepartmentAccess = useCallback((technician, departmentId) => {
    if (!departmentId) return false;
    const departments = technician?.users?.departments;
    if (!departments) return false;

    let parsed = [];
    if (Array.isArray(departments)) {
      parsed = departments;
    } else if (typeof departments === 'string') {
      try {
        parsed = JSON.parse(departments || '[]');
      } catch (error) {
        parsed = [];
      }
    }

    const targetDepartmentId = Number(departmentId);
    return parsed.some((entry) => {
      const candidateId =
        typeof entry === 'object' ? Number(entry?.id ?? entry?.departmentId) : Number(entry);
      return Number.isFinite(candidateId) && candidateId === targetDepartmentId;
    });
  }, []);

  const normalizedWorkOrderCrewIds = useMemo(() => (
    Array.isArray(workOrder?.crew)
      ? workOrder.crew
        .map((entry) => Number(typeof entry === 'object' ? entry?.id : entry))
        .filter(Number.isFinite)
      : []
  ), [workOrder?.crew]);

  const openAddCrewModal = useCallback(async () => {
    try {
      const departmentId = workOrder?.department?.id || workOrder?.departmentId;
      const technicians = await getTechnicians();
      const filtered = (Array.isArray(technicians) ? technicians : []).filter((technician) =>
        hasDepartmentAccess(technician, departmentId),
      );
      setAvailableTechnicians(filtered);
      setSelectedCrewIds(localCrewIds);
      setShowAddCrewModal(true);
    } catch (error) {
      console.error('Failed to load technicians', error);
    }
  }, [hasDepartmentAccess, localCrewIds, workOrder?.department?.id, workOrder?.departmentId]);

  const handleSaveCrew = useCallback(async () => {
    if (!workOrder?.id) return;

    try {
      setSavingCrew(true);
      const nextCrew = Array.from(new Set(selectedCrewIds.map(Number).filter(Number.isFinite)));

      await update('work-orders', workOrder.id, {
        crew: nextCrew,
        updatedBy: userProfile?.id || workOrder?.updatedBy || null,
      });
      setLocalCrewIds(nextCrew);
      setShowAddCrewModal(false);
      setActiveTab('people');
    } catch (error) {
      console.error('Failed to save crew assignment', error);
    } finally {
      setSavingCrew(false);
    }
  }, [selectedCrewIds, userProfile?.id, workOrder]);

  useEffect(() => {
    setLocalCrewIds(normalizedWorkOrderCrewIds);
  }, [workOrder?.id, normalizedWorkOrderCrewIds]);

  useEffect(() => {
    latestWorkOrderIdRef.current = workOrder?.id;
  }, [workOrder?.id]);

  useEffect(() => {
    let isMounted = true;

    const loadWorkOrderComments = async () => {
      if (!workOrder?.id) {
        if (isMounted) {
          setWorkOrderComments([]);
        }
        return;
      }

      try {
        const response = await getWorkOrderCommentsByWorkOrderId(workOrder.id);
        const commentsData = Array.isArray(response?.data) ? response.data : response;
        if (isMounted) {
          setWorkOrderComments(Array.isArray(commentsData) ? commentsData : []);
        }
      } catch (error) {
        console.error('Error loading work order comments', error);
        if (isMounted) {
          setWorkOrderComments([]);
        }
      }
    };

    loadWorkOrderComments();

    return () => {
      isMounted = false;
    };
  }, [workOrder?.id]);

  useEffect(() => {
    setLocalParts(parts);
  }, [parts]);

  useEffect(() => {
    setActiveTab('general');
    setHasLoadedLocationTab(false);
    setLocationCoords(null);
    setIsLoadingLocation(false);
  }, [workOrder?.id]);

  useEffect(() => {
    applyLocationDetails(workOrder);
  }, [
    applyLocationDetails,
    workOrder?.id,
    workOrder?.meetingPoint,
    workOrder?.locationAddress,
    workOrder?.asset?.location?.locationAddress,
  ]);

  useEffect(() => {
    let isMounted = true;
    const loadContractors = async () => {
      try {
        const contractorList = await getContractors();
        if (isMounted) {
          setContractors(Array.isArray(contractorList) ? contractorList : []);
        }
      } catch (error) {
        console.error('Failed to load contractors', error);
        if (isMounted) {
          setContractors([]);
        }
      }
    };

    loadContractors();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadDepartmentHierarchy = async () => {
      const rootDepartmentId = workOrder?.department?.id || workOrder?.departmentId;
      if (!rootDepartmentId) {
        if (isMounted) setDepartmentHierarchyNames([]);
        return;
      }

      const hierarchyNames = [];
      const visited = new Set();
      let nextDepartmentId = rootDepartmentId;

      while (nextDepartmentId && !visited.has(nextDepartmentId)) {
        visited.add(nextDepartmentId);

        try {
          const department = await getHierarchyStructureById(nextDepartmentId);
          if (!department) break;

          if (department.name) {
            hierarchyNames.unshift(department.name);
          }
          nextDepartmentId = department.parentId;
        } catch (error) {
          console.error('Failed to load department hierarchy', error);
          break;
        }
      }

      if (isMounted) {
        setDepartmentHierarchyNames(hierarchyNames);
      }
    };

    loadDepartmentHierarchy();

    return () => {
      isMounted = false;
    };
  }, [workOrder?.department?.id, workOrder?.departmentId]);

  useEffect(() => {
    let isMounted = true;

    const resolveCompletedBy = async () => {
      const completedByName = (workOrder?.completedByName || '').trim();
      if (completedByName) {
        if (isMounted) setCompletedByDisplayName(completedByName);
        return;
      }

      const updatedByUserId = workOrder?.updatedBy;
      if (!updatedByUserId) {
        if (isMounted) setCompletedByDisplayName('Not Assigned');
        return;
      }

      try {
        const user = await getById('users', updatedByUserId);
        const resolvedName = (user?.name || '').trim();
        if (isMounted) setCompletedByDisplayName(resolvedName || 'Not Assigned');
      } catch (error) {
        console.error('Failed to resolve completed by user', error);
        if (isMounted) setCompletedByDisplayName('Not Assigned');
      }
    };

    resolveCompletedBy();

    return () => {
      isMounted = false;
    };
  }, [workOrder?.completedByName, workOrder?.updatedBy]);

  const findPurchaseOrderId = useCallback(async (item, purchaseOrderNumber) => {
    if (!item && !purchaseOrderNumber) return null;
    let purchaseOrderId = item?.purchaseOrderId;
    if (!purchaseOrderId && item?.poLineId) {
      const line = await getById('purchase-order-lines', item.poLineId);
      purchaseOrderId = line?.purchaseOrderId;
    }
    if (!purchaseOrderId && purchaseOrderNumber) {
      const response = await getPaginatedPurchaseOrders({ search: purchaseOrderNumber, pageSize: 25 });
      const orders = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      const match = orders.find(
        (order) => String(order?.poNumber || '').toLowerCase() === String(purchaseOrderNumber).toLowerCase(),
      );
      purchaseOrderId = match?.id;
    }
    return purchaseOrderId || null;
  }, []);

  const handleOpenPurchaseOrderDetails = useCallback(
    async (item, purchaseOrderNumber) => {
      if (!purchaseOrderNumber) return;
      setShowPurchaseOrderDetailsModal(true);
      setIsLoadingPurchaseOrderDetails(true);
      try {
        const purchaseOrderId = await findPurchaseOrderId(item, purchaseOrderNumber);
        if (!purchaseOrderId) {
          setPurchaseOrderDetails(null);
          setPurchaseOrderDetailLines([]);
          return;
        }
        const [order, linesResponse] = await Promise.all([
          getPurchaseOrderById(purchaseOrderId),
          getPurchaseOrderLinesByPurchaseOrderId(purchaseOrderId),
        ]);
        const lines = Array.isArray(linesResponse?.data)
          ? linesResponse.data
          : Array.isArray(linesResponse)
            ? linesResponse
            : [];
        setPurchaseOrderDetails(order || null);
        setPurchaseOrderDetailLines(lines);
      } catch (err) {
        console.error('Failed to load purchase order details', err);
        setPurchaseOrderDetails(null);
        setPurchaseOrderDetailLines([]);
      } finally {
        setIsLoadingPurchaseOrderDetails(false);
      }
    },
    [findPurchaseOrderId],
  );

  const handleAvailableToggle = useCallback(async (partId, nextValue) => {
    if (!partId) return;
    setLocalParts((prev) =>
      prev.map((part) =>
        part.id === partId ? { ...part, available: nextValue ? 1 : 0 } : part,
      ),
    );
    try {
      await update('work-order-parts', partId, { available: nextValue ? 1 : 0 });
    } catch (err) {
      console.error('Failed to update part availability', err);
      setLocalParts((prev) =>
        prev.map((part) =>
          part.id === partId ? { ...part, available: nextValue ? 0 : 1 } : part,
        ),
      );
    }
  }, []);

  const status = currentStatus || workOrder?.status || {};
  const statusId = Number(workOrder.statusId ?? status?.id ?? workOrder.status?.id);
  const showAvailableToggle = statusId === 1;
  const isOverdue =
    workOrder?.dueDate && new Date(workOrder.dueDate) < new Date();
  const COST_COLUMN_STYLE = { textAlign: 'right', whiteSpace: 'nowrap' };
  const TOTAL_COLUMN_STYLE = { width: '90px', textAlign: 'right', whiteSpace: 'nowrap' };
  const hasValue = (value) => value !== null && value !== undefined && value !== '';
  const getNumber = (value) => {
    if (!hasValue(value)) return 0;
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };
  const formatCurrency = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '-';
    return number.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  const displayParts = Array.isArray(localParts) ? localParts : [];
  const areAllPartsReceived = displayParts.length > 0
    && displayParts.every((part) => Number(part?.available ?? part?.received) === 1);
  const partsTotalCost = displayParts.reduce((sum, p) => {
    const cost = getNumber(p.cost ?? p.partsCost ?? p.parts?.cost);
    const quantity = getNumber(p.quantity);
    return sum + cost * quantity;
  }, 0);
  const labourTotalCost = labour.reduce((sum, l) => {
    const rateRaw = l.labourRate ?? l.billOfLabour?.labourRate;
    const labourRate = getNumber(rateRaw);
    const hours = getNumber(l.labourHours);
    const quantity = getNumber(l.qty || 1);
    return sum + labourRate * hours * quantity;
  }, 0);
  const servicesTotalCost = services.reduce((sum, s) => {
    const cost = getNumber(s.cost ?? s.billOfService?.cost ?? s.billOfServices?.cost);
    const quantity = getNumber(s.quantity ?? s.qty);
    return sum + cost * quantity;
  }, 0);

  const actualPartsCost = getNumber(actualCosts?.parts);
  const actualLabourCost = getNumber(actualCosts?.labour);
  const actualMiscCost = getNumber(actualCosts?.misc);
  const showActualPartsCost = actualPartsCost > 0;
  const showActualLabourCost = actualLabourCost > 0;
  const showActualMiscCost = actualMiscCost > 0;
  const locationAddress = (savedLocationAddress || '').trim();
  const shouldLoadLocation = activeTab === 'location' || hasLoadedLocationTab;
  const googleTileUrl = useMemo(() => getGoogleTileUrl(), []);
  const completionImages = useMemo(
    () => (Array.isArray(workOrder?.completionImages) ? workOrder.completionImages : []),
    [workOrder?.completionImages],
  );
  const showResultsTab = useMemo(() => {
    const statusId = Number(workOrder?.statusId ?? workOrder?.status?.id);
    return statusId === 2 || statusId === 4;
  }, [workOrder?.status?.id, workOrder?.statusId]);

  const reloadLocationDetails = useCallback(async () => {
    if (!workOrder?.id) return;
    const requestWorkOrderId = workOrder.id;

    try {
      const latestWorkOrder = await getById('work-orders', requestWorkOrderId);

      if (latestWorkOrderIdRef.current !== requestWorkOrderId) {
        return;
      }

      applyLocationDetails(latestWorkOrder);
    } catch (error) {
      console.error('Failed to reload work order location details', error);
    }
  }, [applyLocationDetails, workOrder?.id]);

  const handleTabChange = useCallback((tabKey) => {
    setActiveTab(tabKey);

    if (tabKey === 'location') {
      setHasLoadedLocationTab(true);
      reloadLocationDetails();
    }
  }, [reloadLocationDetails]);

  useEffect(() => {
    if (!showResultsTab && activeTab === 'results') {
      setActiveTab('general');
    }
  }, [activeTab, showResultsTab]);

  useEffect(() => {
    let isMounted = true;
    const trimmedSearch = editableLocation.trim();

    if (activeTab !== 'location') {
      setAddressSuggestions([]);
      setIsLoadingAddressSuggestions(false);
      return;
    }

    if (trimmedSearch.length < 3) {
      setAddressSuggestions([]);
      setIsLoadingAddressSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoadingAddressSuggestions(true);
      try {
        const data = await getGooglePlaces(trimmedSearch, 5);
        if (isMounted && Array.isArray(data)) {
          setAddressSuggestions(data);
        }
      } catch (error) {
        if (isMounted) {
          setAddressSuggestions([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingAddressSuggestions(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [activeTab, editableLocation]);

  useEffect(() => {
    let isMounted = true;

    const geocodeAddress = async () => {
      if (!shouldLoadLocation) {
        return;
      }

      setIsLoadingLocation(true);
      try {
        if (locationAddress) {
          const result = await getGoogleGeocode(locationAddress);
          const latitude = Number(result?.latitude);
          const longitude = Number(result?.longitude);

          if (isMounted && Number.isFinite(latitude) && Number.isFinite(longitude)) {
            setLocationCoords([latitude, longitude]);
          } else if (isMounted) {
            setLocationCoords(null);
          }
          return;
        }

        const assetId = Number(workOrder?.assetId ?? workOrder?.asset?.id);
        if (!Number.isFinite(assetId)) {
          if (isMounted) setLocationCoords(null);
          return;
        }

        const latestLocation = await getAssetLocationByAssetId(assetId);
        const latitude = Number(latestLocation?.latitude);
        const longitude = Number(latestLocation?.longitude);

        if (isMounted && Number.isFinite(latitude) && Number.isFinite(longitude)) {
          setLocationCoords([latitude, longitude]);
        } else if (isMounted) {
          setLocationCoords(null);
        }
      } catch (error) {
        if (isMounted) {
          setLocationCoords(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingLocation(false);
        }
      }
    };

    geocodeAddress();

    return () => {
      isMounted = false;
    };
  }, [locationAddress, shouldLoadLocation, workOrder?.asset?.id, workOrder?.assetId]);

  const handleSaveLocation = useCallback(async () => {
    if (!workOrder?.id) return;
    const requestWorkOrderId = workOrder.id;
    const trimmedAddress = editableLocation.trim();

    if (trimmedAddress === savedLocationAddress) {
      setShowAddressSuggestions(false);
      return;
    }

    try {
      await update('work-orders', requestWorkOrderId, {
        locationAddress: trimmedAddress,
      });

      if (latestWorkOrderIdRef.current !== requestWorkOrderId) {
        return;
      }

      setSavedLocationAddress(trimmedAddress);
      setEditableLocation(trimmedAddress);
      setShowAddressSuggestions(false);
    } catch (error) {
      console.error('Failed to save work order location', error);
    }
  }, [editableLocation, savedLocationAddress, workOrder?.id]);

  const handleSaveMeetingPoint = useCallback(async () => {
    if (!workOrder?.id) return;
    const requestWorkOrderId = workOrder.id;
    const trimmedMeetingPoint = editableMeetingPoint.trim();

    if (trimmedMeetingPoint === savedMeetingPoint) {
      return;
    }

    try {
      await update('work-orders', requestWorkOrderId, {
        meetingPoint: trimmedMeetingPoint,
      });

      if (latestWorkOrderIdRef.current !== requestWorkOrderId) {
        return;
      }

      setSavedMeetingPoint(trimmedMeetingPoint);
      setEditableMeetingPoint(trimmedMeetingPoint);
    } catch (error) {
      console.error('Failed to save work order meeting point', error);
    }
  }, [editableMeetingPoint, savedMeetingPoint, workOrder?.id]);

  if (!workOrder) return null;

  return (
    <>
      <div className="d-flex align-items-center">
        <div className="btn-group" role="group">
          {statuses
            .filter((s) => s.id !== 5)
            .map((s) => (
              <button
                key={s.id}
                type="button"
                className={`btn btn-sm ${status?.id === s.id ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => onStatusChange && onStatusChange(s.id)}
              >
                <div className="d-flex flex-column align-items-center mt-2" style={{ width: '65px' }}>
                  <Icon icon={s.icon} style={{ fontSize: '24px', color: 'inherit' }} />
                  <span className="mt-6">{s.name}</span>
                </div>
              </button>
            ))}
        </div>
        {/*{showAssignContractor && (
          <div className="ms-auto">
            <button className="btn text-primary" onClick={() => setShowShareModal(true)}>
              <Icon
                icon="mdi:share-variant-outline"
                className="me-8"
                style={{ position: "relative", top: "0px", fontSize: '24px' }}
              />
              <span style={{ position: "relative", top: "-1px" }}>Assign Work</span>
            </button>
          </div>)}*/}
      </div>
      <ul className="nav nav-tabs mb-6 mt-12" role="tablist">
        {[
          { key: 'general', label: 'General' },
          { key: 'people', label: 'People' },
          { key: 'resources', label: 'Resources' },
          { key: 'location', label: 'Location' },
          ...(showResultsTab ? [{ key: 'results', label: 'Results' }] : []),
          { key: 'comments', label: 'Comments' },
        ].map((tab) => (
          <li className="nav-item" key={tab.key} role="presentation">
            <button
              type="button"
              role="tab"
              className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
              {tab.key === 'comments' && workOrderCommentCount > 0 && (
                <span
                  className="badge bg-danger text-white"
                  style={{
                    position: 'relative',
                    top: '-8px',
                    left: '2px',
                    borderRadius: '50%',
                    minWidth: '18px',
                    height: '18px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.55rem',
                  }}
                >
                  {workOrderCommentCount}
                </span>
              )}
            </button>
          </li>
        ))}
        <li className="nav-item ms-auto d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm fw-semibold">Parts Ready</span>
            <div className="form-switch switch-success m-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={areAllPartsReceived}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm fw-semibold">Job Ready</span>
            <div className="form-switch switch-success m-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={Number(workOrder?.jobReadyStatus) === 1}
                disabled={!canUpdateWorkOrder}
                onChange={(e) => onJobReadyStatusChange && onJobReadyStatusChange(e.target.checked ? 1 : 0)}
              />
            </div>
          </div>
          {showCurrentStatus && (
            <span className={`badge bg-${workOrder.status.colours.bootstrapName} text-md`}>
              {workOrder.status.name}
            </span>
          )}
        </li>
      </ul>

      {activeTab === 'general' && (
        <>
          <div className="row">
            {(workOrder.asset) && (
              <div className="col-md-9 mb-16">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <img
                    style={{ width: '65px', height: '65px', borderRadius: '8px', border: '1px solid #ddd', objectFit: 'cover' }}
                    src={
                      workOrder.asset?.assetImage
                        ? `${process.env.REACT_APP_SPACES_BASE}/${workOrder.asset.assetImage}`
                        : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`
                    }
                    alt="Asset"
                  />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="fw-bold">
                      {`${workOrder.asset?.assetNumber || ''} - ${workOrder.asset?.assetDescription || '-'}`}
                    </div>
                    {workOrder.asset?.assetSerial ?
                      <div className="text-muted text-sm">
                        {workOrder.asset?.assetSerial} {workOrder.asset?.assetFloc ? ` | ${workOrder.asset.assetFloc}` : ''}
                      </div> : null}
                    {departmentHierarchyNames.length > 0
                      ? departmentHierarchyNames.join(' > ')
                      : workOrder?.department?.name || '—'}
                  </div>

                </div>
              </div>
            )}
            {!workOrder.assetId && (workOrder.areaId || workOrder.area) && (
              <div className="col-md-9 mb-4">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <img
                    style={{ width: '65px', height: '65px', borderRadius: '8px', border: '1px solid #ddd', objectFit: 'cover' }}
                    src={
                      workOrder.area?.image
                        ? `${process.env.REACT_APP_SPACES_BASE}/${workOrder.area.image}`
                        : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`
                    }
                    alt="Area"
                  />
                  <strong>
                    {`${workOrder.area?.code || ''} - ${workOrder.area?.name || '-'}`}
                  </strong>
                  {workOrder.area?.floc ? `(${workOrder.area.floc})` : ''}
                </div>
              </div>
            )}
            <div className="col-md-3 mb-16 ms-auto text-md-end">
              <div className="fw-bold mb-3">Priority</div>
              {workOrder.priority && (
                <>
                  <span
                    className={`d-inline-block rounded-circle bg-${workOrder.priority.colours?.bootstrapName || 'secondary'} me-1`}
                    style={{ width: 10, height: 10 }}
                  ></span>
                  {workOrder.priority.name}
                </>
              )}
            </div>
            <div className="col-12">
              <hr style={{ borderTop: '1px solid #444', margin: '0 0 0px 0' }} />
            </div>
            <div className="col-md-12 mt-12 mb-8">
              <div className="d-flex flex-row align-items-start gap-2" style={{ minHeight: "40px" }}>
                {isOverdue && workOrder.statusId !== 4 && (
                  <div className="alert alert-danger px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="mdi:alert-circle-outline" className="text-xl text-danger me-6" style={{ position: 'relative', top: '-3px' }} />
                    Overdue
                  </div>
                )}
                {workOrder.breakdown === 1 && ( // Breakdown
                  <div className="alert alert-danger px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="ic:baseline-bolt" className="text-xl text-danger me-6" style={{ position: 'relative', top: '-3px' }} />
                    Breakdown
                  </div>
                )}
                {workOrder.statutory === 1 && (
                  <div className="alert alert-warning px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="mdi:alert-rhombus-outline" className="text-xl text-alert me-6" style={{ position: 'relative', top: '-3px' }} />
                    Compliance Task
                  </div>
                )}
                {workOrder.workOrderScheduleId ?
                  <div className="alert alert-primary px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="mdi:backup-restore" className="text-xl text-primary me-6" style={{ position: 'relative', top: '-3px' }} />
                    Recurring Work
                  </div> : null
                }
                {workOrder.taskId && workOrder?.task?.schedulingType !== 'cycle' ?
                  <div className="alert alert-primary px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="mdi:calendar-task-outline" className="text-xl text-primary me-6" style={{ position: 'relative', top: '-3px' }} />
                    Linked to a Task
                  </div> : null
                }
                {workOrder.taskId && workOrder?.task?.schedulingType === 'cycle' ?
                  <div className="alert alert-primary px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="mdi:calendar-refresh-outline" className="text-xl text-primary me-6" style={{ position: 'relative', top: '-3px' }} />
                    Linked to a Cycle Task
                  </div> : null
                }
                {workOrder.eventId && workOrder.eventId !== '' ? (
                  <div className="alert alert-primary px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="mdi:calendar-month-outline" className="text-xl text-primary me-6" style={{ position: 'relative', top: '-3px' }} />
                    Linked to an Event
                  </div>
                ) : null
                }
                {((workOrder.taskId && workOrder.taskId !== '') || null) && workOrder?.taskType?.component === 1 && (
                  <div className="alert alert-warning px-8 py-4 w-auto mb-0" role="alert">
                    <Icon icon="mage:settings" className="text-xl text-warning me-6" style={{ position: 'relative', top: '-3px' }} />
                    Component
                  </div>
                )}
                {((workOrder.taskId && workOrder.taskId !== '') || null) && workOrder.task?.rotablesId !== null && (
                  <div className="alert alert-warning px-8 py-4 w-auto mb-0 text-alert" role="alert">
                    <Icon icon="mage:settings" className="text-xl text-alert me-6" style={{ position: 'relative', top: '-3px' }} />
                    Rotable
                  </div>
                )}
              </div>
            </div>
            <div className="col-12">
              <hr style={{ borderTop: '1px solid #444', margin: '0 0 0 0' }} />
            </div>
            <div className="col-md-4 mb-16">
              <div className="fw-bold mb-8">Scheduled Start</div>
              <Icon icon="mdi:calendar-blank-outline" className="text-primary text-lg me-6" style={{ position: "relative", top: "-4px" }} />
              {workOrder.scheduledAt ? formatIntDateTime(workOrder.scheduledAt) : '—'}
            </div>
            <div className="col-md-4 mb-16">
              <div className="fw-bold mb-8">Due Date</div>
              <Icon icon="mdi:calendar-blank-outline" className="text-primary text-lg me-6" style={{ position: "relative", top: "-4px" }} />
              {workOrder.dueDate ? formatIntDateTime(workOrder.dueDate) : '—'}
            </div>
            <div className="col-md-4 mb-16">
              <div className="fw-bold mb-8">Created Date</div>
              <Icon icon="mdi:calendar-blank-outline" className="text-primary text-lg me-6" style={{ position: "relative", top: "-4px" }} />
              {formatIntDateTime(workOrder.createdAt)}
            </div>
            {status?.name === 'In Progress' && (
              <div className="col-md-12 mb-16 mt-0">
                <div className="fw-bold mb-8">Start Date</div>
                <Icon icon="mdi:calendar-blank-outline" className="text-primary text-lg me-6" style={{ position: "relative", top: "-4px" }} />
                {formatIntDateTime(workOrder.startedAt)}
              </div>
            )}

            {(Number(workOrder?.status) === 4 || Number(workOrder?.statusId) === 4) && (
              <>
                <div className="col-md-4 mb-4">
                  <div className="fw-bold mb-8">Started At</div>
                  <Icon icon="mdi:calendar-blank-outline" className="text-primary text-lg me-6" style={{ position: "relative", top: "-4px" }} />
                  {formatIntDateTime(workOrder.startedAt)}
                </div>
                <div className="col-md-4 mb-4">
                  <div className="fw-bold mb-8">Completed At</div>
                  <Icon icon="mdi:calendar-blank-outline" className="text-primary text-lg me-6" style={{ position: "relative", top: "-4px" }} />
                  {formatIntDateTime(workOrder.completedAt)}
                </div>
                <div className="col-md-4 mb-4">
                  <div className="fw-bold mb-8">Completed By</div>
                  <Icon icon="mdi:account-outline" className="text-primary text-lg me-6" style={{ position: "relative", top: "-4px" }} />
                  {completedByDisplayName || 'Guest User'}
                </div>
              </>
            )}
            <div className="col-12">
              <hr style={{ borderTop: '1px solid #444', margin: '0 0 0 0' }} />
            </div>
            <div className="col-md-12">
              <div className="fw-bold mb-8">Description</div>
              {workOrder.workOrderDescription || '—'}

              {Array.isArray(workOrder?.workOrderImage) && workOrder.workOrderImage.length > 0 && (
                <div className="mt-3">
                  {workOrder.workOrderImage.map((img, index) => (
                    <img
                      key={index}
                      src={`${process.env.REACT_APP_SPACES_BASE}/${img}`}
                      alt={`Work Order ${index + 1}`}
                      className="me-2 mb-2"
                      style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain' }}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="col-12 mt-3">
              <hr style={{ borderTop: '1px solid #444', margin: 0 }} />
            </div>
            <div className="my-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="fw-bold">Forms</div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => onAddForm && onAddForm(workOrder)}
                  disabled={workOrder.statusId === 4}
                >
                  + Add Form or Inspection
                </button>
              </div>
              {forms.length > 0 ? (
                <div className="row g-3 mt-16 mx-16">
                  {forms.map((f) => {
                    const imageUrl = f.form?.image
                      ? `${process.env.REACT_APP_SPACES_BASE}/${f.form.image}`
                      : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                    return (
                      <div key={f.id} className="col-md-4 col-xxl-3">

                        <div
                          className="hover-scale-img border radius-10 overflow-hidden position-relative"
                          onClick={() => onViewForm && onViewForm(f)}
                        >
                          <div className="max-h-266-px overflow-hidden">
                            <img src={imageUrl} alt="" className="hover-scale-img__img w-100 object-fit-cover" style={{ height: '120px', objectFit: 'cover' }} />
                          </div>
                          <div className="mt-12 pb-6 px-12">
                            {f.completed === 1 && (
                              <Icon
                                icon="mage:check-circle-fill"
                                className="icon text-success text-xl position-relative"
                                style={{ top: "-10px" }}
                              />
                            )}
                            <span className="ms-8 text-truncate" style={{ maxWidth: "200px" }}><strong>{f.formName || f.form?.name}</strong></span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : 'No Forms Attached'}
            </div>
            <div className="col-12">
              <hr style={{ borderTop: '1px solid #444', margin: 0 }} />
            </div>
            <div className="col-md-4 mb-16">
              <div className="fw-bold mb-8">Task Type</div>
              {workOrder.taskType?.taskTypeName || '—'}
            </div>
          </div>
        </>
      )}
            {activeTab === 'people' && (
        <>
          <div className="col-md-4 mb-4">
            <div className="fw-bold mb-8">Team Assigned</div>
            {workOrder.team ? (
              <>
                <span
                  className={`d-inline-flex align-items-center justify-content-center rounded-circle text-white me-8 bg-${workOrder.team.colours?.bootstrapName || 'secondary'}`}
                  style={{ width: 32, height: 32 }}
                >
                  {(workOrder.team.teamName || '')
                    .split(' ')
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase()}
                </span>
                {workOrder.team.teamName}
              </>
            ) : 'No Team Assigned'}
          </div>
          <div className="col-12">
            <hr style={{ borderTop: '1px solid #444', margin: '0 0 20px 0' }} />
          </div>
          <div className="col-md-12 mb-16">
            <div className="d-flex align-items-center justify-content-between mb-8">
              <div className="fw-bold">Crew Assigned</div>
              {canUpdateWorkOrder && (
                <button type="button" className="btn btn-primary btn-sm" onClick={openAddCrewModal}>
                  + Add / Edit Crew
                </button>
              )}
            </div>
            {(() => {
              const crewAssigned = localCrewIds.length > 0
                ? localCrewIds
                  .map((member) => {
                    if (typeof member === 'object') {
                      return {
                        id: member.id,
                        name: member.name,
                        image: member.userImage,
                      };
                    }

                    const crewMember = crew.find((c) => c.id === member);
                    return {
                      id: member,
                      name: crewMember?.name || `User ${member}`,
                      image: crewMember?.userImage,
                    };
                  })
                  .filter((member) => member?.name)
                : [];

              if (crewAssigned.length === 0) {
                return <div>No Crew Assigned</div>;
              }

              return (
                <div className="d-flex flex-column">
                  {crewAssigned.map((member, index) => (
                    <div key={member.id || index} className="d-flex align-items-center mb-24">
                      <img
                        src={profileImageUrl(member.image)}
                        alt={member.name}
                        width={25}
                        height={25}
                        className="rounded-circle me-8"
                        style={{ objectFit: 'cover', width: "25px", height: "25px" }}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_USER_AVATAR;
                        }}
                      />
                      <span>{member.name}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <div className="col-12">
            <hr style={{ borderTop: '1px solid #444', margin: '0 0 20px 0' }} />
          </div>
          <div className="col-md-4 mb-16">
            <div className="fw-bold mb-8">Contractors</div>
            {(() => {
              const contractorAssigned = Array.isArray(workOrder.contractors) && workOrder.contractors.length > 0
                ? workOrder.contractors
                  .map((member) => {
                    if (typeof member === 'object') {
                      return {
                        id: member.id,
                        name: member.name,
                        image: member.profileImage,
                      };
                    }

                    const contractor = contractors.find((c) => c.id === member);
                    return {
                      id: member,
                      name: contractor?.name || `User ${member}`,
                      image: contractor?.profileImage,
                    };
                  })
                  .filter((member) => member?.name)
                : [];

              if (contractorAssigned.length === 0) {
                return <div>No Contractors Assigned</div>;
              }

              return (
                <div className="d-flex flex-column">
                  {contractorAssigned.map((member, index) => (
                    <div key={member.id || index} className="d-flex align-items-center mb-24">
                      <img
                        src={profileImageUrl(member.image)}
                        alt={member.name}
                        width={25}
                        height={25}
                        className="rounded-circle me-8"
                        style={{ objectFit: 'cover' }}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_USER_AVATAR;
                        }}
                      />
                      <span>{member.name}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </>
      )}
      {showAddCrewModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add / Edit Crew</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddCrewModal(false)} />
              </div>
              <div className="modal-body">
                <div className="list-group">
                  {availableTechnicians.map((technician) => {
                    const techUserId = Number(technician?.userId);
                    const teamNames = Array.isArray(technician?.teams)
                      ? technician.teams.map((team) => (typeof team === 'object' ? team?.teamName : '')).filter(Boolean)
                      : [];
                    const checked = selectedCrewIds.includes(techUserId);
                    return (
                      <label key={technician.id} className="list-group-item d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={checked}
                          onChange={() =>
                            setSelectedCrewIds((prev) =>
                              checked ? prev.filter((id) => id !== techUserId) : [...prev, techUserId],
                            )
                          }
                        />
                        <img
                          src={profileImageUrl(technician?.users?.userImage)}
                          alt={technician?.users?.name || 'Technician'}
                          width={24}
                          height={24}
                          className="rounded-circle"
                        />
                        <div className="d-flex flex-column">
                          <span>{technician?.users?.name || `User ${techUserId}`}</span>
                          <small className="text-muted">{teamNames.join(', ') || 'No Teams Assigned'}</small>
                        </div>
                      </label>
                    );
                  })}
                  {!availableTechnicians.length && (
                    <div className="text-muted">No technicians available for this department.</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddCrewModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingCrew}
                  onClick={handleSaveCrew}
                >
                  {savingCrew ? 'Saving...' : 'Save Crew'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'resources' && (
        <>
          <div className="my-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-bold">Parts</div>
            </div>
            {displayParts.length > 0 ? (
              <table className="table bordered-table table-sm text-md table-bordered mt-16 mx-16 w-95">
                <thead>
                  <tr>
                    <th>Part Number</th>
                    <th>OEM Number</th>
                    <th>Description</th>
                    <th>Source</th>
                    <th>Status</th>
                    {showAvailableToggle && <th className="text-center">Received</th>}
                    <th>Qty</th>
                    <th className="text-end" style={COST_COLUMN_STYLE}>Cost</th>
                    <th className="text-end" style={COST_COLUMN_STYLE}>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {displayParts.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ width: "120px" }}><strong>{p.parts?.partNumber}</strong></td>
                      <td style={{ width: "120px" }}>{p.parts?.oemNumber}</td>
                      <td>{p.parts?.description}</td>
                      <td style={{ width: "120px" }}>
                        {p.source === 'WAREHOUSE' ? (
                          'Warehouse'
                        ) : p.source === 'PURCHASE ORDER' && p.poNumber ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 text-primary"
                            onClick={() => handleOpenPurchaseOrderDetails(p, p.poNumber)}
                          >
                            {p.poNumber}
                          </button>
                        ) : (
                          p.poNumber || '-'
                        )}
                      </td>
                      <td style={{ width: "110px" }}>
                        {p.status === 'Reserved' && <span className="badge bg-success">{p.status}</span>}
                        {p.status === 'Ordered' && <span className="badge bg-success">{p.status}</span>}
                        {p.status === 'Backorder' && <span className="badge bg-primary">{p.status}</span>}
                        {p.status === 'PendingApproval' && <span className="badge bg-warning">{p.status}</span>}
                        {p.status === 'Draft' && <span className="badge bg-secondary">{p.status}</span>}
                        {p.status === 'Approved' && <span className="badge bg-success">{p.status}</span>}
                        {p.status === 'OnOrder' && <span className="badge bg-primary">{p.status}</span>}
                      </td>
                      {showAvailableToggle && (
                        <td style={{ width: "110px" }} className="text-center">
                          <div className="form-switch d-inline-flex align-items-center">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id={`part-available-${p.id ?? idx}`}
                              checked={Number(p.available) === 1}
                              onChange={(event) => handleAvailableToggle(p.id, event.target.checked)}
                              disabled={!p.id}
                            />
                          </div>
                        </td>
                      )}
                      <td style={{ width: "70px" }}>{p.quantity}</td>
                      <td className="text-end" style={COST_COLUMN_STYLE}>
                        {hasValue(p.cost ?? p.partsCost ?? p.parts?.cost)
                          ? formatCurrency(p.cost ?? p.partsCost ?? p.parts?.cost)
                          : '-'}
                      </td>
                      <td className="text-end" style={COST_COLUMN_STYLE}>
                        {hasValue(p.cost ?? p.partsCost ?? p.parts?.cost)
                          ? formatCurrency(
                            getNumber(p.quantity) *
                            getNumber(p.cost ?? p.partsCost ?? p.parts?.cost),
                          )
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td
                      colSpan={showAvailableToggle ? 8 : 7}
                      className="text-end"
                      style={{ borderTop: "1px solid var(--neutral-200)" }}>
                      Total
                    </td>
                    <td
                      style={{
                        ...TOTAL_COLUMN_STYLE,
                        borderTop: "1px solid var(--neutral-200)"
                      }}
                    >
                      {formatCurrency(partsTotalCost)}
                    </td>
                  </tr>
                  {showActualPartsCost && (
                    <tr className="fw-bold">
                      <td colSpan={showAvailableToggle ? 8 : 7} className="text-end" style={{ borderTop: "1px solid var(--neutral-200)" }}>
                        Actual Cost
                      </td>
                      <td className="text-end" style={{ ...TOTAL_COLUMN_STYLE, borderTop: "1px solid var(--neutral-200)" }}>{formatCurrency(actualPartsCost)}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            ) : (
              <>
                <p className="mt-4 ms-16">No parts on this work order</p>
                <div className="col-12">
                  <hr style={{ borderTop: '1px solid #444', margin: '0 0 20px 0' }} />
                </div>
              </>
            )}
          </div>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-bold">Labour</div>
            </div>
            {labour.length > 0 ? (
              <table className="table bordered-table table-sm text-md table-bordered mt-16 mx-16 w-95">
                <thead>
                  <tr>
                    <th>Trade</th>
                    <th>Qty</th>
                    <th>Hours</th>
                    <th className="text-end" style={COST_COLUMN_STYLE}>Rate</th>
                    <th className="text-end" style={COST_COLUMN_STYLE}>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {labour.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.trade?.name}</td>
                      <td>{l.qty}</td>
                      <td>{l.labourHours}</td>
                      <td className="text-end" style={COST_COLUMN_STYLE}>
                        {hasValue(l.labourRate ?? l.billOfLabour?.labourRate)
                          ? formatCurrency(l.labourRate ?? l.billOfLabour?.labourRate)
                          : '-'}
                      </td>
                      <td className="text-end" style={COST_COLUMN_STYLE}>
                        {hasValue(l.labourRate ?? l.billOfLabour?.labourRate)
                          ? formatCurrency(
                            getNumber(l.qty || 1) *
                            getNumber(l.labourHours) *
                            getNumber(l.labourRate ?? l.billOfLabour?.labourRate),
                          )
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td colSpan="4" className="text-end" style={{ borderTop: "1px solid var(--neutral-200)" }}>
                      Total
                    </td>
                    <td className="text-end" style={{ ...TOTAL_COLUMN_STYLE, borderTop: "1px solid var(--neutral-200)" }}>{formatCurrency(labourTotalCost)}</td>
                  </tr>
                  {showActualLabourCost && (
                    <tr className="fw-bold">
                      <td colSpan="4" className="text-end" style={{ borderTop: "1px solid var(--neutral-200)" }}>
                        Actual Cost
                      </td>
                      <td className="text-end" style={{ ...TOTAL_COLUMN_STYLE, borderTop: "1px solid var(--neutral-200)" }}>{formatCurrency(actualLabourCost)}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            ) : (
              <>
                <p className="mt-4 ms-16">No labour on this work order</p>
                <div className="col-12">
                  <hr style={{ borderTop: '1px solid #444', margin: '0 0 20px 0' }} />
                </div>
              </>
            )}
          </div>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-bold">Other Costs</div>
            </div>
            {services.length > 0 ? (
              <table className="table bordered-table table-sm text-md table-bordered mt-16 mx-16 w-95">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Description</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Qty</th>
                    <th className="text-end" style={COST_COLUMN_STYLE}>Cost</th>
                    <th className="text-end" style={COST_COLUMN_STYLE}>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s, idx) => (
                    <tr key={idx}>
                      <td style={{ width: "110px" }}>{s.service?.name}</td>
                      <td>{s.description}</td>
                      <td style={{ width: "110px" }}>
                        {s.poNumber ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 text-primary"
                            onClick={() => handleOpenPurchaseOrderDetails(s, s.poNumber)}
                          >
                            {s.poNumber}
                          </button>
                        ) : (
                          s.poNumber || '-'
                        )}
                      </td>
                      <td style={{ width: "110px" }}>
                        {s.status === 'Reserved' && <span className="badge bg-success">{s.status}</span>}
                        {s.status === 'Ordered' && <span className="badge bg-success">{s.status}</span>}
                        {s.status === 'Backorder' && <span className="badge bg-primary">{s.status}</span>}
                        {s.status === 'PendingApproval' && <span className="badge bg-warning">{s.status}</span>}
                        {s.status === 'Draft' && <span className="badge bg-secondary">{s.status}</span>}
                        {s.status === 'Approved' && <span className="badge bg-success">{s.status}</span>}
                        {s.status === 'OnOrder' && <span className="badge bg-primary">{s.status}</span>}
                      </td>
                      <td style={{ width: "70px" }}>{s.quantity}</td>
                      <td className="text-end" style={COST_COLUMN_STYLE}>
                        {hasValue(s.cost ?? s.billOfService?.cost ?? s.billOfServices?.cost)
                          ? formatCurrency(s.cost ?? s.billOfService?.cost ?? s.billOfServices?.cost)
                          : '-'}
                      </td>
                      <td className="text-end" style={COST_COLUMN_STYLE}>
                        {hasValue(s.cost ?? s.billOfService?.cost ?? s.billOfServices?.cost)
                          ? formatCurrency(
                            getNumber(s.quantity ?? s.qty) *
                            getNumber(s.cost ?? s.billOfService?.cost ?? s.billOfServices?.cost),
                          )
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td colSpan="6" className="text-end" style={{ borderTop: "1px solid var(--neutral-200)" }}>
                      Total
                    </td>
                    <td className="text-end" style={{ ...TOTAL_COLUMN_STYLE, borderTop: "1px solid var(--neutral-200)" }}>{formatCurrency(servicesTotalCost)}</td>
                  </tr>
                  {showActualMiscCost && (
                    <tr className="fw-bold">
                      <td colSpan="6" className="text-end" style={{ borderTop: "1px solid var(--neutral-200)" }}>
                        Actual Cost
                      </td>
                      <td className="text-end" style={{ ...TOTAL_COLUMN_STYLE, borderTop: "1px solid var(--neutral-200)" }}>{formatCurrency(actualMiscCost)}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            ) : (
              <>
                <p className="mt-4 ms-16">No other costs on this work order</p>
                <div className="col-12">
                  <hr style={{ borderTop: '1px solid #444', margin: '0 0 20px 0' }} />
                </div>
              </>
            )}
          </div>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-bold">Tools & Resources</div>
            </div>
            {equipment.length > 0 ? (
              <table className="table bordered-table table-sm  text-md table-bordered mt-16 mx-16 w-95">
                <thead>
                  <tr><th>Name</th><th>Qty</th></tr>
                </thead>
                <tbody>
                  {equipment.map((e, idx) => (
                    <tr key={idx}>
                      <td>{e.name}</td>
                      <td>{e.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <>
                <p className="mt-4 ms-16">No equipment on this work order</p>
              </>
            )}
          </div>
        </>
      )}
      {(activeTab === 'location' || hasLoadedLocationTab) && (
        <div className={`mb-3 ${activeTab === 'location' ? '' : 'd-none'}`}>
          <div className="d-flex gap-16 flex-wrap">
            <div className="mb-12 flex-grow-1" style={{ width: '20%', minWidth: 200 }}>
              <label htmlFor="meeting-point" className="form-label">Meeting Point</label>
              <input
                id="meeting-point"
                type="text"
                className="form-control"
                value={editableMeetingPoint}
                onChange={(event) => setEditableMeetingPoint(event.target.value)}
                onBlur={handleSaveMeetingPoint}
                placeholder="Enter meeting point"
              />
            </div>
            <div className="mb-12 position-relative flex-grow-1" style={{ width: '40%', minWidth: 300 }}>
              <label htmlFor="meeting-point" className="form-label">Address</label>
              <div className="d-flex gap-8">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Australian address"
                  value={editableLocation}
                  onChange={(event) => {
                    setEditableLocation(event.target.value);
                    setShowAddressSuggestions(true);
                  }}
                  onFocus={() => setShowAddressSuggestions(true)}
                  onBlur={handleSaveLocation}
                />
              </div>
              {showAddressSuggestions && (isLoadingAddressSuggestions || addressSuggestions.length > 0) && (
                <div
                  className="bg-white border rounded mt-4"
                  style={{ maxHeight: 220, overflowY: 'auto', position: 'absolute', width: '100%', zIndex: 20 }}
                >
                  {isLoadingAddressSuggestions && (
                    <div className="px-12 py-8 text-muted">Searching addresses...</div>
                  )}
                  {!isLoadingAddressSuggestions && addressSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.placeId}
                      type="button"
                      className="btn btn-link text-start text-decoration-none d-block w-100 px-12 py-8"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setEditableLocation(suggestion.description || '');
                      }}
                      onClick={() => {
                        setShowAddressSuggestions(false);
                      }}
                    >
                      {suggestion.description}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="text-muted mb-12">
            {locationAddress || 'No location address available. Displaying Australia map.'}
          </div>
          {isLoadingLocation && <div className="mb-12">Loading location...</div>}
          <div style={{ height: 360, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd' }}>
            <MapContainer
              center={locationCoords || AUSTRALIA_DEFAULT_CENTER}
              zoom={locationCoords ? 14 : AUSTRALIA_DEFAULT_ZOOM}
              scrollWheelZoom={false}
              style={{ height: '100%', width: '100%' }}
              key={`${locationCoords?.[0] || 'au'}-${locationCoords?.[1] || 'default'}`}
            >
              <TileLayer
                attribution="&copy; Google"
                url={googleTileUrl}
              />
              {locationCoords && (
                <Marker position={locationCoords}>
                  <Popup>{locationAddress}</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        </div>
      )}
      {activeTab === 'results' && (
        <>
          <div className="col-md-12 mb-8">
            <div className="fw-bold mb-8">Notes</div>
            {workOrder.notes || '—'}
            <div className="col-md-12 mb-4">
              {completionImages.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mt-12">
                  {completionImages.map((image, index) => {
                    const imageUrl = completionImageUrl(image);
                    if (!imageUrl) return null;
                    return (
                      <a
                        key={`${image?.id || image?.filename || image?.name || 'completion-image'}-${index}`}
                        href={imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border radius-8 overflow-hidden d-inline-block"
                        style={{ width: '96px', height: '96px' }}
                      >
                        <img
                          src={imageUrl}
                          alt={`Completion ${index + 1}`}
                          className="w-100 h-100 object-fit-cover"
                        />
                      </a>
                    );
                  })}
                </div>
              ) : (
                '—'
              )}
            </div>
          </div>
          <div className="col-12">
            <hr style={{ borderTop: '1px solid #444', margin: '0 0 20px 0' }} />
          </div>
          <div className="fw-bold text-xl">Failure Details</div>
          <div className="col-md-6 mb-4">
            <div className="fw-bold mb-8">Failure Code</div>
            {`${workOrder.failure?.code || ''} — ${workOrder.failure?.name || ''}` || '—'}
          </div>
          <div className="col-md-6 mb-4">
            <div className="fw-bold mb-8">Cause Code</div>
            {`${workOrder.cause?.code || ''} — ${workOrder.cause?.name || ''}` || '—'}
          </div>
          <div className="col-md-12 mb-4">
            <div className="fw-bold mb-8">Symptom</div>
            {workOrder.symptom || '—'}
          </div>
        </>
      )}
      {activeTab === 'comments' && (
        <WorkOrderCommentsSection
          workOrderId={workOrder.id}
          initialComments={workOrderComments}
          onCommentsChange={setWorkOrderComments}
          className="mt-4"
        />
      )}
      {showShareModal && (
        <WorkOrderShare
          show={showShareModal}
          workOrder={workOrder}
          onClose={() => setShowShareModal(false)}
          onSaved={(nextWorkPackage) => setSharedWorkPackage(nextWorkPackage)}
        />
      )}
      {showPurchaseOrderDetailsModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl" style={{ top: '5vh' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Purchase Order Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowPurchaseOrderDetailsModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  {isLoadingPurchaseOrderDetails ? (
                    <div>Loading purchase order details...</div>
                  ) : (
                    <PurchaseOrderPrintView
                      order={purchaseOrderDetails}
                      orderLines={purchaseOrderDetailLines}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default WorkOrderDetails;
